// Mirrors domain.RiskyShellCommandPrefixes on the backend. Commands whose
// first token matches one of these get an operator acknowledgement step —
// the backend no longer blocks anything.
export const RISKY_SHELL_PREFIXES: ReadonlySet<string> = new Set([
  'rm', 'reboot', 'su', 'chmod', 'chown', 'mount', 'umount',
  'dd', 'mkfs', 'format', 'shutdown', 'kill', 'killall',
  'flash', 'fastboot', 'adb', 'setenforce',
  'echo', 'write', 'mv', 'cp', 'mkdir', 'rmdir',
  'iptables', 'ip6tables', 'nftables',
]);

export function isCommandRisky(command: string): boolean {
  const trimmed = command.trim();
  if (!trimmed) return false;
  const firstToken = trimmed.split(/\s+/)[0];
  return RISKY_SHELL_PREFIXES.has(firstToken);
}

// Detects commands that run indefinitely by nature (no built-in stop
// condition). They'll be killed by the device-side timeout, but the
// signal-to-noise is bad: stdout is usually empty and the user just
// sees "command failed". Warn before they fire, and suggest a fix.
export type LongRunHit = { match: false } | { match: true; reason: string; suggestion: string };
export function isLongRunning(command: string): LongRunHit {
  const trimmed = command.trim().toLowerCase();
  if (!trimmed) return { match: false };

  // ping <host>  →  needs -c N to stop
  if (/^ping\b/.test(trimmed) && !/\s-c\s+\d+/.test(trimmed)) {
    return { match: true, reason: 'ping with no count', suggestion: 'add `-c 4` to limit packets' };
  }
  // tail -f / -F
  if (/^tail\b.*\s-[fF]\b/.test(trimmed)) {
    return { match: true, reason: 'tail -f streams forever', suggestion: 'drop -f, or wrap in `timeout 30 …`' };
  }
  // top / htop with no -n
  if (/^h?top\b/.test(trimmed) && !/\s-n\s+\d+/.test(trimmed)) {
    return { match: true, reason: 'top runs interactively', suggestion: 'add `-n 1` for a single snapshot' };
  }
  // watch
  if (/^watch\b/.test(trimmed)) {
    return { match: true, reason: 'watch loops forever', suggestion: 'run the inner command once instead' };
  }
  // yes
  if (/^yes\b/.test(trimmed)) {
    return { match: true, reason: '`yes` emits forever', suggestion: 'pipe to `head -n N`' };
  }
  // nc / netcat listener
  if (/^(nc|netcat)\b.*\s-l\b/.test(trimmed)) {
    return { match: true, reason: 'netcat listener blocks', suggestion: 'use a finite probe instead' };
  }
  // bare cat (reads stdin)
  if (/^cat\s*$/.test(trimmed)) {
    return { match: true, reason: '`cat` with no args reads stdin', suggestion: 'pass a file path' };
  }
  // sleep N — flag when it exceeds the max selectable timeout (2min)
  const sleepMatch = trimmed.match(/^sleep\s+(\d+)([smhd])?/);
  if (sleepMatch) {
    const v = parseInt(sleepMatch[1], 10);
    const u = sleepMatch[2] || 's';
    const secs = u === 's' ? v : u === 'm' ? v * 60 : u === 'h' ? v * 3600 : v * 86400;
    if (secs > 120) {
      return { match: true, reason: `sleep ${secs}s exceeds the 2min max`, suggestion: 'lower the duration' };
    }
  }
  // explicit infinite loops
  if (/\bwhile\s+(true|:|\[\s*1\s*\])|\bfor\s*\(\s*;\s*;\s*\)|\buntil\s+false\b/.test(trimmed)) {
    return { match: true, reason: 'infinite loop', suggestion: 'add an exit condition' };
  }
  // logcat without -d (dump-and-exit)
  if (/^logcat\b/.test(trimmed) && !/\s-d\b/.test(trimmed)) {
    return { match: true, reason: 'logcat streams forever', suggestion: 'add `-d` to dump and exit' };
  }
  // dmesg -w (follow) / journalctl -f
  if (/^dmesg\b.*\s-w\b/.test(trimmed)) {
    return { match: true, reason: 'dmesg -w follows forever', suggestion: 'drop -w for a one-shot dump' };
  }
  if (/^journalctl\b.*\s-f\b/.test(trimmed)) {
    return { match: true, reason: 'journalctl -f follows forever', suggestion: 'drop -f, or add `-n 200`' };
  }

  return { match: false };
}

/**
 * How long to wait for a device to answer, offered the same way wherever a
 * shell command is sent.
 *
 * There used to be two lists: the device page's 5/15/30/60/120 s in
 * milliseconds, and bulk shell's 10/30/60/120 in seconds. Same decision, two
 * sets of choices and two units, so "30 s" on one screen and "30 s" on the
 * other were only equal by coincidence.
 *
 * This is the union of both, not a new set. "Run again…" prefills from the
 * old job, and a value missing here falls back to the default — so dropping
 * 10 s would quietly turn every re-run of a 10 s job into a 30 s one.
 */
export const SHELL_TIMEOUT_OPTIONS: { ms: number; label: string }[] = [
  { ms: 5_000, label: '5 s' },
  { ms: 10_000, label: '10 s' },
  { ms: 15_000, label: '15 s' },
  { ms: 30_000, label: '30 s' },
  { ms: 60_000, label: '60 s' },
  { ms: 120_000, label: '2 min' },
];

export const DEFAULT_SHELL_TIMEOUT_MS = 30_000;

/** timeout_ms of a run that waits for no answer: send and forget. */
export const NO_TIMEOUT = 0;

/**
 * Bulk Shell's timeouts: the device shell's, plus No timeout. Only bulk runs
 * know 0: the device shell's endpoint turns it into its 30 s default.
 */
export const RUN_TIMEOUT_OPTIONS = [...SHELL_TIMEOUT_OPTIONS, { ms: NO_TIMEOUT, label: 'No timeout' }];

/** How long a no-timeout run waits before its next batch. */
export const SLOT_RELEASE_OPTIONS: { ms: number; label: string }[] = [
  { ms: 30_000, label: '30 s' },
  { ms: 60_000, label: '1 min' },
  { ms: 300_000, label: '5 min' },
  { ms: 900_000, label: '15 min' },
];
export const DEFAULT_SLOT_RELEASE_MS = 60_000;
export const slotReleaseLabel = (ms: number) => SLOT_RELEASE_OPTIONS.find((o) => o.ms === ms)?.label ?? `${Math.round(ms / 1000)} s`;
